import { motion } from "framer-motion";
import { Send, Rocket } from "lucide-react";
import { FaXTwitter, FaTelegram } from "react-icons/fa6";
import { useTranslation, Trans } from "react-i18next";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useState } from "react";
import { COIN } from "@/lib/constants";

const SOCIAL_META = [
  { name: "Telegram",    href: COIN.socials.telegram, Icon: FaTelegram,  color: "text-secondary", descKey: "telegram_desc" },
  { name: "Twitter / X", href: COIN.socials.twitter,  Icon: FaXTwitter,  color: "text-foreground", descKey: "twitter_desc" },
  { name: "Pump.fun",    href: COIN.socials.pumpfun,  Icon: Rocket,      color: "text-primary",    descKey: "pumpfun_desc" },
];

export default function Community() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!email.match(/.+@.+\..+/)) {
      toast.error(t("community.newsletter_invalid"));
      return;
    }
    toast.success(t("community.newsletter_success"));
    setEmail("");
  };

  return (
    <section id="community" data-testid="community-section" className="relative py-24 md:py-32 bg-black/40">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("community.section_label")}</p>
            <h2 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-6xl leading-[0.95]">
              {t("community.title_l1")}
              <br />
              {t("community.title_l2_prefix")} <span className="text-primary neon-glow-primary">{t("community.title_l2_suffix")}</span>
            </h2>
            <p className="mt-6 max-w-md text-muted-foreground">{t("community.description")}</p>

            <form onSubmit={submit} className="mt-10 space-y-3">
              <label className="font-mono-alt text-[10px] tracking-widest text-muted-foreground">
                {t("community.newsletter_label")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("community.newsletter_placeholder")}
                  data-testid="newsletter-input"
                  className="flex-1 rounded-sm border border-primary/30 bg-black/60 px-4 py-3 text-sm font-mono-alt text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
                />
                <Button
                  type="submit"
                  data-testid="newsletter-submit"
                  className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs px-5"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </motion.div>

          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {SOCIAL_META.map((s, i) => (
              <motion.a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`social-link-${s.name.toLowerCase().replace(/[^a-z]/g, "")}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group glass rounded-sm border border-primary/25 p-6 hover:border-primary/60 transition-colors flex flex-col justify-between min-h-[220px]"
              >
                <s.Icon className={`h-10 w-10 ${s.color} group-hover:scale-110 transition-transform`} />
                <div>
                  <p className="font-mono-alt text-[10px] tracking-widest text-muted-foreground">{t("community.channel_label")}</p>
                  <p className="mt-1 font-display font-black text-2xl tracking-tight">{s.name}</p>
                  <p className="mt-2 text-sm text-muted-foreground">{t(`community.socials.${s.descKey}`)}</p>
                </div>
              </motion.a>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="sm:col-span-3 relative rounded-sm border border-primary/25 bg-primary/5 p-8 overflow-hidden"
            >
              <Rocket className="absolute -right-10 -bottom-10 h-56 w-56 text-primary/10" />
              <p className="font-mono-alt text-[10px] tracking-[0.3em] text-primary">{t("community.warning_label")}</p>
              <p className="mt-3 font-display font-bold text-xl md:text-2xl">
                <Trans i18nKey="community.warning_html" components={{ b: <span className="text-primary" /> }} />
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
